import { useNavigate } from "react-router-dom";
import { login } from "../../firebase/auth";
import { PLANS } from "../../features/subscription/planConfig";
import { getDemoVideoEmbedUrl } from "../../config/media";
import useDocumentHead from "../../hooks/useDocumentHead";
import Logo from "../common/Logo";

const FREE_PLAN = PLANS.free;

const HOW_IT_WORKS = [
  {
    step: "1",
    title: "Upload your spreadsheet",
    description: "Drag in a CSV or Excel file, or click to browse. No templates or special formatting required.",
  },
  {
    step: "2",
    title: "InsightPulse profiles your data",
    description: "Columns, data types, and the overall shape of your dataset are detected automatically in seconds.",
  },
  {
    step: "3",
    title: "Ask questions in plain English",
    description: "\u201cWhich region grew fastest last quarter?\u201d No SQL, no formulas, no pivot tables.",
  },
  {
    step: "4",
    title: "Get insights, dashboards & reports",
    description: "Charts build themselves, and you can generate AI-written executive summaries and detailed reports.",
  },
];

const FEATURES = [
  {
    icon: "\ud83d\udcac",
    title: "Conversational Data Analysis",
    description: "Ask questions about your data using natural language and get direct, data-backed answers — the way you'd ask a colleague, not a spreadsheet.",
  },
  {
    icon: "\ud83d\udd0d",
    title: "Automatic Data Profiling",
    description: "Every upload is scanned for column types, measures, dimensions, and data quality issues before you ask a single question.",
  },
  {
    icon: "\u2728",
    title: "AI-Generated Business Insights",
    description: "InsightPulse surfaces the trends, outliers, and standouts in your data automatically — not just the ones you thought to look for.",
  },
  {
    icon: "\ud83d\udcca",
    title: "Interactive Dashboards & Charts",
    description: "Explore your dataset visually with dashboards and charts generated straight from your spreadsheet, no chart-building required.",
  },
  {
    icon: "\ud83d\udcc4",
    title: "AI Business Reports & Executive Summaries",
    description: "Turn a conversation into a polished, shareable report or executive summary in a couple of clicks.",
  },
  {
    icon: "\ud83d\udcec",
    title: "Scheduled PDF Reports",
    description: "Schedule a one-time report from your analysis and InsightPulse turns it into a polished PDF with key metrics and AI-generated insights, delivered straight to your recipients.",
    badge: "Premium",
    points: [
      "Schedule one-time reports",
      "Share with multiple recipients",
      "Professional PDF format",
      "AI-generated insights & key metrics",
    ],
  },
];

const USE_CASES = [
  {
    icon: "\ud83d\udcc8",
    title: "Sales & Revenue Analysis",
    description: "Track performance by product, region, or time period, and find out what's driving — or dragging down — revenue.",
  },
  {
    icon: "\ud83d\udc65",
    title: "Customer Insights",
    description: "Understand who your best customers are, how they behave, and where the biggest opportunities are hiding.",
  },
  {
    icon: "\ud83d\udce6",
    title: "Product Performance",
    description: "See which products are winning, which are underperforming, and why — down to category and unit-level detail.",
  },
  {
    icon: "\ud83c\udfe2",
    title: "Business & Operational Reporting",
    description: "Turn raw operational spreadsheets into executive-ready summaries without a dedicated analyst.",
  },
];

const DEMO_WORKFLOW_STEPS = [
  "Upload data",
  "Automatically understand the dataset",
  "Explore dashboards and charts",
  "Ask questions using natural language",
  "Generate insights and reports",
];

const FAQ_ITEMS = [
  {
    question: "What file formats does InsightPulse AI support?",
    answer: "InsightPulse accepts CSV and Excel files (.csv, .xls, .xlsx). Just upload your file the way it already exists — no reformatting needed.",
  },
  {
    question: "Do I need to know SQL, Excel formulas, or a BI tool?",
    answer: "No. You ask questions in plain English and InsightPulse handles the analysis. There's nothing to configure and no query language to learn.",
  },
  {
    question: "Is there a free plan?",
    answer: `Yes. The Free plan includes ${FREE_PLAN.monthlyAnalysisLimit} analyses per month and supports files up to ${FREE_PLAN.maxFileSizeMB} MB — no credit card required. Premium raises those limits and removes report watermarks.`,
  },
  {
    question: "Can I try InsightPulse without uploading my own data or signing up?",
    answer: "Yes. Click \u201cExplore Demo\u201d to instantly open a realistic retail sales dataset in the full product — dashboards, insights, and one AI-powered question — with no account required. Once you're signed in, \u201cTry with sample data\u201d lets you explore the same dataset with unlimited questions.",
  },
  {
    question: "Is my data secure?",
    answer: "Your files and sessions are tied to your account and are not shared with other users. See our Privacy Policy below for full details on how your data is handled.",
  },
];

function SectionHeading({ eyebrow, title, subtitle, center = true }) {
  return (
    <div className={`max-w-2xl ${center ? "mx-auto text-center" : ""} mb-10 sm:mb-14`}>
      {eyebrow && (
        <div className="text-xs font-semibold tracking-wider text-blue-400 uppercase mb-3">
          {eyebrow}
        </div>
      )}
      <h2 className="text-2xl sm:text-3xl font-bold text-white">{title}</h2>
      {subtitle && (
        <p className="mt-3 text-gray-400 leading-relaxed">{subtitle}</p>
      )}
    </div>
  );
}

export default function Homepage() {
  const navigate = useNavigate();

  useDocumentHead({
    title: "InsightPulse AI — AI-Powered Data Analysis for CSV & Excel Files",
    description:
      "Upload a CSV or Excel file, ask questions in plain English, and get automated dashboards, AI-generated business insights, and executive-ready reports with InsightPulse AI.",
  });

  const scrollToId = (id) => (e) => {
    e.preventDefault();
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const demoEmbedUrl = getDemoVideoEmbedUrl();

  return (
    <div className="min-h-screen bg-gray-950 text-white overflow-x-hidden">

      {/* ===================================== */}
      {/* NAV */}
      {/* ===================================== */}
      <header className="sticky top-0 z-40 border-b border-gray-800/80 bg-gray-950/85 backdrop-blur-xl">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-4">
          <div className="flex items-center gap-2.5 min-w-0">
            <Logo size={28} />
            <span className="font-bold text-white tracking-tight truncate">InsightPulse AI</span>
          </div>

          <nav className="hidden md:flex items-center gap-6 text-sm text-gray-400">
            <a href="#how-it-works" onClick={scrollToId("how-it-works")} className="hover:text-white transition">How it works</a>
            <a href="#features" onClick={scrollToId("features")} className="hover:text-white transition">Features</a>
            <a href="#demo" onClick={scrollToId("demo")} className="hover:text-white transition">Demo</a>
            <a href="#faq" onClick={scrollToId("faq")} className="hover:text-white transition">FAQ</a>
          </nav>

          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={() => navigate("/demo")}
              className="hidden sm:inline-flex text-sm text-gray-300 hover:text-white px-3 py-2 rounded-lg hover:bg-gray-900 transition font-medium"
            >
              Explore Demo
            </button>
            <button
              onClick={login}
              className="hidden sm:inline-flex text-sm text-gray-300 hover:text-white px-3 py-2 rounded-lg hover:bg-gray-900 transition font-medium"
            >
              Sign in
            </button>
            <button
              onClick={login}
              className="text-sm bg-blue-600 hover:bg-blue-700 transition px-4 py-2 rounded-xl font-semibold text-white shadow-lg shadow-blue-900/30"
            >
              Get Started Free
            </button>
          </div>
        </div>
      </header>

      {/* ===================================== */}
      {/* HERO */}
      {/* ===================================== */}
      <section className="relative px-4 sm:px-6 pt-16 sm:pt-24 pb-16 sm:pb-20">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-10 left-1/4 w-72 h-72 bg-blue-600/10 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-72 h-72 bg-purple-600/10 rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 text-xs font-medium text-blue-300 bg-blue-500/10 border border-blue-500/20 rounded-full px-3 py-1.5 mb-6">
            <span className="w-1.5 h-1.5 rounded-full bg-blue-400" />
            AI Data Analysis for Spreadsheets
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight tracking-tight">
            Turn spreadsheets into
            <span className="block bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
              business insights, instantly
            </span>
          </h1>

          <p className="mt-6 text-lg text-gray-400 leading-relaxed max-w-2xl mx-auto">
            InsightPulse AI is an AI-powered data analyst for your CSV and Excel files. Upload your spreadsheet,
            ask questions in plain English, and get clear business insights, dashboards, and reports —
            no formulas, no BI tools, no code.
          </p>

          <div className="mt-9 flex flex-col sm:flex-row items-center justify-center gap-3">
            <button
              onClick={login}
              className="w-full sm:w-auto px-7 py-3.5 rounded-xl bg-blue-600 hover:bg-blue-700 transition font-semibold text-white shadow-lg shadow-blue-900/30"
            >
              Get Started Free
            </button>
            <button
              onClick={() => navigate("/demo")}
              className="w-full sm:w-auto px-7 py-3.5 rounded-xl bg-gray-900 hover:bg-gray-800 border border-gray-800 hover:border-gray-700 transition font-semibold text-gray-200 text-center"
            >
              Try Demo Data — No Sign-Up
            </button>
          </div>

          <p className="mt-5 text-xs text-gray-500">
            Free plan available &middot; No credit card required &middot; Works with CSV, XLS, and XLSX
          </p>
          <p className="mt-2 text-xs text-gray-500">
            <a href="#demo" onClick={scrollToId("demo")} className="underline hover:text-gray-300 transition">
              {"\u25b6"} Or watch a video walkthrough
            </a>
          </p>
        </div>
      </section>

      {/* ===================================== */}
      {/* WHAT IS INSIGHTPULSE */}
      {/* ===================================== */}
      <section className="px-4 sm:px-6 py-14 border-t border-gray-800/80 bg-gray-900/30">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-white">What is InsightPulse AI?</h2>
          <p className="mt-5 text-gray-400 leading-relaxed">
            InsightPulse AI is an AI data analyst built for teams who work in spreadsheets. Instead of writing
            formulas or learning a BI tool, you upload a CSV or Excel file and describe what you want to know.
            InsightPulse automatically profiles your data, highlights the patterns that matter, and lets you
            explore sales, revenue, customer, and product performance through natural conversation — turning
            raw spreadsheet data into insights your whole team can act on.
          </p>
        </div>
      </section>

      {/* ===================================== */}
      {/* HOW IT WORKS */}
      {/* ===================================== */}
      <section id="how-it-works" className="px-4 sm:px-6 py-16 sm:py-20 scroll-mt-16">
        <div className="max-w-6xl mx-auto">
          <SectionHeading
            eyebrow="How it works"
            title="From raw spreadsheet to business insight in minutes"
            subtitle="No setup, no data pipeline, no BI license — just upload and ask."
          />

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {HOW_IT_WORKS.map((item) => (
              <div key={item.step} className="bg-gray-900 border border-gray-800 rounded-2xl p-5">
                <div className="w-9 h-9 rounded-lg bg-blue-600/15 border border-blue-500/20 text-blue-400 font-bold flex items-center justify-center mb-4">
                  {item.step}
                </div>
                <h3 className="font-semibold text-white">{item.title}</h3>
                <p className="mt-2 text-sm text-gray-400 leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===================================== */}
      {/* CORE FEATURES */}
      {/* ===================================== */}
      <section id="features" className="px-4 sm:px-6 py-16 sm:py-20 border-t border-gray-800/80 bg-gray-900/30 scroll-mt-16">
        <div className="max-w-6xl mx-auto">
          <SectionHeading
            eyebrow="Core features"
            title="Everything you need to understand your data"
            subtitle="AI spreadsheet analysis that goes from raw rows to a finished report."
          />

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {FEATURES.map((feature) => (
              <div key={feature.title} className="bg-gray-900 border border-gray-800 rounded-2xl p-6 hover:border-gray-700 transition flex flex-col">
                <div className="flex items-start justify-between gap-2">
                  <div className="text-2xl" aria-hidden="true">{feature.icon}</div>
                  {feature.badge && (
                    <span className="text-[10px] font-semibold text-gray-300 bg-white/10 px-2 py-0.5 rounded-full shrink-0 mt-1">
                      {feature.badge}
                    </span>
                  )}
                </div>
                <h3 className="font-semibold text-white mt-3">{feature.title}</h3>
                <p className="mt-2 text-sm text-gray-400 leading-relaxed">{feature.description}</p>
                {feature.points && (
                  <ul className="mt-3 space-y-1.5">
                    {feature.points.map((point) => (
                      <li key={point} className="flex items-center gap-2 text-xs text-gray-400">
                        <span className="text-emerald-500" aria-hidden="true">✓</span>
                        {point}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===================================== */}
      {/* USE CASES */}
      {/* ===================================== */}
      <section className="px-4 sm:px-6 py-16 sm:py-20">
        <div className="max-w-6xl mx-auto">
          <SectionHeading
            eyebrow="Use cases"
            title="Built around the data teams already have"
            subtitle="If it's in a spreadsheet, InsightPulse can help you make sense of it."
          />

          <div className="grid sm:grid-cols-2 gap-5">
            {USE_CASES.map((useCase) => (
              <div key={useCase.title} className="flex gap-4 bg-gray-900 border border-gray-800 rounded-2xl p-6">
                <div className="text-2xl shrink-0" aria-hidden="true">{useCase.icon}</div>
                <div>
                  <h3 className="font-semibold text-white">{useCase.title}</h3>
                  <p className="mt-1.5 text-sm text-gray-400 leading-relaxed">{useCase.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===================================== */}
      {/* DEMO VIDEO */}
      {/* ===================================== */}
      <section id="demo" className="px-4 sm:px-6 py-16 sm:py-20 border-t border-gray-800/80 bg-gray-900/30 scroll-mt-16">
        <div className="max-w-5xl mx-auto">
          <SectionHeading
            eyebrow="Demo"
            title="See InsightPulse AI in action"
            subtitle="Watch the full workflow — from a raw spreadsheet to dashboards, insights, and reports."
          />

          <div className="grid lg:grid-cols-[1fr_320px] gap-8 items-start">

            {/* Video */}
            <div className="w-full aspect-video rounded-2xl overflow-hidden border border-gray-800 bg-black shadow-2xl">
              {demoEmbedUrl ? (
                <iframe
                  className="w-full h-full"
                  src={demoEmbedUrl}
                  title="InsightPulse AI demo video"
                  loading="lazy"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              ) : (
                <div className="w-full h-full flex flex-col items-center justify-center text-center px-6 bg-gray-900">
                  <div className="text-3xl mb-3" aria-hidden="true">{"\ud83c\udfa5"}</div>
                  <p className="text-gray-300 font-medium">Demo video coming soon</p>
                  <p className="text-gray-500 text-sm mt-1">Check back shortly, or get started free below.</p>
                </div>
              )}
            </div>

            {/* Workflow steps */}
            <ol className="space-y-4">
              {DEMO_WORKFLOW_STEPS.map((step, i) => (
                <li key={step} className="flex items-start gap-3">
                  <span className="shrink-0 w-6 h-6 rounded-full bg-blue-600/15 border border-blue-500/20 text-blue-400 text-xs font-bold flex items-center justify-center mt-0.5">
                    {i + 1}
                  </span>
                  <span className="text-sm text-gray-300 leading-relaxed pt-0.5">{step}</span>
                </li>
              ))}
            </ol>

          </div>
        </div>
      </section>

      {/* ===================================== */}
      {/* FAQ */}
      {/* ===================================== */}
      <section id="faq" className="px-4 sm:px-6 py-16 sm:py-20 scroll-mt-16">
        <div className="max-w-3xl mx-auto">
          <SectionHeading title="Frequently asked questions" />

          <div className="space-y-3">
            {FAQ_ITEMS.map((item) => (
              <details key={item.question} className="group bg-gray-900 border border-gray-800 rounded-2xl overflow-hidden">
                <summary className="cursor-pointer list-none px-5 py-4 flex items-center justify-between gap-4 text-sm font-medium text-gray-200">
                  {item.question}
                  <span className="shrink-0 text-gray-500 transition-transform group-open:rotate-45 text-lg leading-none">+</span>
                </summary>
                <div className="px-5 pb-4 text-sm text-gray-400 leading-relaxed">
                  {item.answer}
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* ===================================== */}
      {/* GET STARTED / SIGN IN */}
      {/* ===================================== */}
      <section id="get-started" className="px-4 sm:px-6 py-16 sm:py-20 border-t border-gray-800/80 scroll-mt-16">
        <div className="max-w-md mx-auto">
          <div className="bg-gray-900/90 backdrop-blur-xl border border-gray-800 rounded-3xl p-8 sm:p-10 shadow-2xl">

            <div className="mb-6">
              <Logo size={56} />
            </div>

            <h2 className="text-3xl font-bold text-white mb-3">Get started with InsightPulse AI</h2>

            <p className="text-gray-400 leading-relaxed mb-7">
              Sign in and turn your first spreadsheet into insights in minutes — or explore instantly with a
              sample dataset if you don't have a file handy.
            </p>

            <div className="space-y-3 mb-8 text-sm">
              <div className="flex items-center gap-3 text-gray-300">
                <span className="text-green-400">{"\u2713"}</span>
                Upload CSV and Excel files
              </div>
              <div className="flex items-center gap-3 text-gray-300">
                <span className="text-green-400">{"\u2713"}</span>
                Generate dashboards automatically
              </div>
              <div className="flex items-center gap-3 text-gray-300">
                <span className="text-green-400">{"\u2713"}</span>
                Ask questions about your data
              </div>
            </div>

            <button
              onClick={login}
              className="w-full bg-blue-600 hover:bg-blue-700 transition-all duration-200 py-4 rounded-xl font-semibold text-white shadow-lg"
            >
              Continue with Google
            </button>

            <p className="text-gray-500 text-xs text-center mt-6">
              Secure authentication powered by Google
            </p>

            <p className="text-gray-500 text-xs text-center mt-4">
              Not ready to sign up?{" "}
              <button
                type="button"
                onClick={() => navigate("/demo")}
                className="underline text-gray-300 hover:text-white transition"
              >
                Explore the demo instead
              </button>
            </p>

            <p className="text-gray-600 text-[11px] text-center mt-3">
              By continuing you agree to our{" "}
              <button
                type="button"
                onClick={() => navigate("/terms")}
                className="underline hover:text-gray-400 transition"
              >
                Terms & Conditions
              </button>{" "}
              and{" "}
              <button
                type="button"
                onClick={() => navigate("/privacy")}
                className="underline hover:text-gray-400 transition"
              >
                Privacy Policy
              </button>
              .
            </p>

          </div>
        </div>
      </section>

      {/* ===================================== */}
      {/* FOOTER */}
      {/* ===================================== */}
      <footer className="px-4 sm:px-6 py-8 border-t border-gray-800/80">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-gray-500">
          <div className="flex items-center gap-2">
            <Logo size={20} />
            <span>InsightPulse AI</span>
          </div>

          <div className="flex items-center gap-x-4 gap-y-1 flex-wrap justify-center">
            <span>&copy; {new Date().getFullYear()} InsightPulse AI</span>
            <button onClick={() => navigate("/privacy")} className="hover:text-gray-300 transition">
              Privacy Policy
            </button>
            <button onClick={() => navigate("/terms")} className="hover:text-gray-300 transition">
              Terms & Conditions
            </button>
            <button onClick={() => navigate("/refund-policy")} className="hover:text-gray-300 transition">
              Refund Policy
            </button>
          </div>
        </div>
      </footer>

    </div>
  );
}
